import { useState } from "react";
import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, makeStyles, Menu, MenuItem, Select } from "@material-ui/core";
import download from "downloadjs";
import DetailsDialog, { calculateMode, DetailsMode, DetailsProps } from "../DetailsDialog";
import DateInputField, { getCurrentDate } from "../ui/DateInputField";
import TextInputField from "../ui/TextInputField";
import OrderItemsAccordion from "./OrderItemsAccordion";
import Http from "../../engine/Http";
import { Identifiable } from "../../model/Items";
import useKitsPrintout, { } from "../kit/KitsPrintout";
import { Order, OrderStatus } from "../../api/OrderApi";
import PriceListSelection from "../pricing/PriceListSelection";
import useOrderSpringApi, { PocketSpring } from "../../api/OrderSpringApi";
import { BonnellSpring } from "../bonnellspring/BonnellSpringDetails";
import { SpringMaterial } from "../../api/SpringMaterialApi";
import { OrderPocketSpring } from "../../api/OrderSpringApi";
import useSpringMaterialCustomerPriceApi from "../../api/SpringMaterialCustomerPriceApi";
import { formatSpringName } from "../../model/Springs";
import useHttp from "../../security/Http.hook";

interface Metrics {
  weight: number;
  unitPrice: number;
}

type IdentifiableMetricsMap = {
  [key: number]: Metrics
}

interface Props extends DetailsProps {
  customerId: number;
}

interface OrderMaterial {
  name: string;
  weight: number;
  total: number;
}

interface OrderItem {
  name: string;
  itemId: number;
  quantityOrdered: number;
  quantityDelivered: number;
  unitPrice: number;
  material?: OrderMaterial;
}

interface PrintableOrderItem {
  name: string;
  quantityOrdered: number;
  quantityDelivered?: number;
  unitPrice: number;
  material?: OrderMaterial;
}

interface Price {
  effective: number;
}

interface MetricsIdentifiable extends Identifiable {
  weight: number;
  unitWeight: number;
  price: Price;
  unitPrice: Price;
}

interface KitMetric {
  packagingPrice: number;
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: 800
  },
  button: {
    marginTop: theme.spacing(1)
  }
}));

export default (props: Props) => {
  const classes = useStyles();
  const http = useHttp();
  const kitsPrintout = useKitsPrintout();
  const orderSpringApi = useOrderSpringApi();
  const springMaterialCustomerPriceApi = useSpringMaterialCustomerPriceApi();
  const mode = calculateMode(props.instanceId);
  const [orderId, setOrderId] = useState<number|null>(null);
  const [number, setNumber] = useState<string>();
  const isOrderAlreadyCreated = mode !== DetailsMode.Create || (orderId !== null && orderId !== undefined);
  const [isOrderNumberGeneratedAutomatically, setOrderNumberGeneratedAutomatically] = useState<boolean>(!isOrderAlreadyCreated);
  const [automaticallyGeneratedOrderNumber, setAutomaticallyGeneratedOrderNumber] = useState<number|undefined>();
  const [creationDate, setCreationDate] = useState<string>();
  const [completionDate, setCompletionDate] = useState<string>();
  const [orderStatus, setOrderStatus] = useState<OrderStatus>();
  const customerId = props.customerId;
  const effectiveOrderId = orderId || props.instanceId;
  const ordersResource = `customers/${customerId}/orders`;
  const [printoutsMenuAnchorElement, setPrintoutsMenuAnchorElement] = useState<null|HTMLElement>(null);
  const isPrintoutsMenuOpen = printoutsMenuAnchorElement !== null;
  const [isModificationDisabled, setModificationDisabled] = useState<boolean>(false);
  const [priceListId, setPriceListId] = useState<number|null>(null);

  const convertToPrintableOrderItem = (orderItem: any, itemType: string) : OrderItem => {
    const item = orderItem[itemType];
    return {
      name: item.name,
      itemId: item.id,
      quantityOrdered: orderItem.quantityOrdered,
      quantityDelivered: orderItem.quantityDelivered,
      unitPrice: orderItem.unitPrice,
      material: item.material
    }
  };

  const getSpringMaterialPrintableName = (material: SpringMaterial) => `${material.name} (${material.thickness} mm)`;

  const convertOrderBonnellSpringToPrintableOrderItem = (orderBonnellSpring: any) : PrintableOrderItem => {
    const bonnellSpring: BonnellSpring = orderBonnellSpring.bonnellSpring;
    return {
      name: formatSpringName(bonnellSpring.name, bonnellSpring.width, bonnellSpring.length, []),
      quantityOrdered: orderBonnellSpring.quantityOrdered,
      quantityDelivered: orderBonnellSpring.quantityDelivered,
      unitPrice: orderBonnellSpring.unitPrice,
      material: {
        name: getSpringMaterialPrintableName(bonnellSpring.material),
        weight: 0,
        total: 0
      }
    }
  };

  const convertOrderPocketSpringToPrintableOrderItem = (orderPocketSpring: OrderPocketSpring) : PrintableOrderItem => {
    const pocketSpring: PocketSpring = orderPocketSpring.pocketSpring;
    const zones = pocketSpring.zones;
    const zoneMaterialNames = zones
      .map(zone => zone.material)
      .map(getSpringMaterialPrintableName);
    const totalZoneLength = sum(zones.map(zone => zone.length));
    return {
      name: formatSpringName(pocketSpring.name, pocketSpring.width, totalZoneLength, []),
      quantityOrdered: orderPocketSpring.quantityOrdered,
      quantityDelivered: orderPocketSpring.quantityDelivered,
      unitPrice: orderPocketSpring.unitPrice,
      material: {
        name: Array.from(new Set(zoneMaterialNames)).join(', '),
        weight: 0,
        total: 0
      }
    }
  };

  const getByIdSortOrder = (item: any, anotherItem: any) => item.id - anotherItem.id;

  const fetchOrderForms = async (orderId: number) => {
    const orderForms: any[] = await http.get(`/api/${ordersResource}/${orderId}/forms`);
    return orderForms
      .sort(getByIdSortOrder)
      .map(form => convertToPrintableOrderItem(form, 'form'));
  };

  const fetchOrderShapes = async (orderId: number) => {
    const orderShapes: any[] = await http.get(`/api/${ordersResource}/${orderId}/shapes`);
    return orderShapes
      .sort(getByIdSortOrder)
      .map(shape => convertToPrintableOrderItem(shape, 'shape'));
  };

  const fetchOrderKits = async (orderId: number) => {
    const orderKits: any[] = await http.get(`/api/${ordersResource}/${orderId}/kits`);
    return orderKits
      .sort(getByIdSortOrder)
      .map(kit => convertToPrintableOrderItem(kit, 'kit'));
  };

  const fetchOrderBonnellSprings = async (orderId: number) => {
    const orderBonnellSprings: any[] = await http.get(`/api/${ordersResource}/${orderId}/bonnell-springs`);
    return orderBonnellSprings
      .sort(getByIdSortOrder)
      .map(convertOrderBonnellSpringToPrintableOrderItem);
  };

  const fetchOrderPocketSpringPrintableOrderItems = async (orderId: number): Promise<PrintableOrderItem[]> => {
    const pocketSprings = await orderSpringApi.fetchOrderPocketSprings(customerId, orderId);
    return pocketSprings
      .sort(getByIdSortOrder)
      .map(convertOrderPocketSpringToPrintableOrderItem);
  };

  const preparePrintableOrder = async (orderId: number) => {
    const customer = await http.get(`/api/customers/${customerId}`);
    const orderForms = await fetchOrderForms(orderId);
    const orderShapes = await fetchOrderShapes(orderId);
    const orderKits = await fetchOrderKits(orderId);
    const orderBonnellSprings = await fetchOrderBonnellSprings(orderId);
    const orderPocketSprings = await fetchOrderPocketSpringPrintableOrderItems(orderId);
    return {
      customer,
      number: number,
      creationDate: creationDate,
      completionDate: completionDate,
      status: orderStatus,
      forms: orderForms,
      shapes: orderShapes,
      kits: orderKits,
      bonnellSprings: orderBonnellSprings,
      pocketSprings: orderPocketSprings
    }
  };

  const closePrintoutsMenu = () => setPrintoutsMenuAnchorElement(null);

  const handlePrintoutsButtonClicked = (event: React.MouseEvent<HTMLElement>) => setPrintoutsMenuAnchorElement(event.currentTarget);

  const executeOrderPrintout = async(order: any, orderType: string, fileName: string) =>
    Http
      .postForFile(`/api/printouts/${orderType}`, order)
      .then(response => response.blob())
      .then(blob => download(blob, fileName))
      .then(() => closePrintoutsMenu());

  const handlePrintInternalOrderButtonClicked = async (orderId: number, kitDetailsIncluded: boolean) => {
    const order = await preparePrintableOrder(orderId);
    let orderWithKitDetails;
    if (kitDetailsIncluded && priceListId !== null) {
      const kits = await kitsPrintout.getKitsForPrintout(customerId, priceListId, order.kits.map(orderItem => orderItem.itemId));
      orderWithKitDetails = {
        ...order,
        customerKits: {
          mode: 'kits',
          customer: order.customer.name,
          kits
        }
      };
    } else {
      orderWithKitDetails = order;
    }
    return executeOrderPrintout(orderWithKitDetails, 'orders', 'zamówienie-produkcja.pdf');
  };

  const handlePrintOfficeOrderButtonClicked = async (orderId: number) => {
    const order = await preparePrintableOrder(orderId);
    return executeOrderPrintout(order, 'officeorders', 'zamówienie-biuro.pdf');
  };

  const handlePrintCustomerOrderButtonClicked = async (
    orderId: number,
    foamMaterialsIncluded: boolean,
    springMaterialsIncluded: boolean
  ) => {
    const order = await preparePrintableOrder(orderId);
    const total = calculateTotal([...order.forms, ...order.shapes, ...order.kits, ...order.bonnellSprings, ...order.pocketSprings]);
    let orderWithMaterials;
    if (foamMaterialsIncluded) {
      const formMetrics = await getMetricsMap('forms');
      const shapeMetrics = await getMetricsMap('shapes');
      const kitForms = await fetchKitFormOrderItems(order.kits, formMetrics);
      const kitShapes = await fetchKitShapeOrderItems(order.kits, shapeMetrics);
      const kitMetrics = await fetchKitMetrics(order.kits);
      const packagingPrice = sum(kitMetrics.map(kitMetric => kitMetric.packagingPrice));
      const materialBonnellSprings = springMaterialsIncluded ? order.bonnellSprings : [];
      const orderedPocketSpringZones = springMaterialsIncluded ? await getOrderedZonesOfOrderPocketSprings(orderId) : [];
      const materials = await calculateOrderMaterials(
        [...order.forms, ...kitForms],
        formMetrics,
        [...order.shapes, ...kitShapes],
        shapeMetrics,
        materialBonnellSprings,
        orderedPocketSpringZones);
      console.debug(`Materials=${JSON.stringify(materials)}`);
      orderWithMaterials = {
        ...order,
        total: Number(total.toFixed(2)),
        packagingPrice: Number(packagingPrice.toFixed(2)),
        materials
      };
    } else {
      orderWithMaterials = {
        ...order,
        total: Number(total.toFixed(2))
      };
    }
    return executeOrderPrintout(orderWithMaterials, 'customerorders', 'zamówienie-klient.pdf');
  };

  const getOrderedZonesOfOrderPocketSprings = async (orderId: number): Promise<PrintableOrderItem[]> => {
    const materialUnitPrices = await getSpringMaterialIdIndexedUnitPrices();
    const orderPocketSprings = await orderSpringApi.fetchOrderPocketSprings(customerId, orderId);
    const pricedZones: PrintableOrderItem[] = [];
    orderPocketSprings
      .forEach(orderPocketSpring => orderPocketSpring.pocketSpring.zones
        .forEach(zone => pricedZones.push({
          name: orderPocketSpring.pocketSpring.name,
          quantityOrdered: orderPocketSpring.quantityOrdered,
          quantityDelivered: orderPocketSpring.quantityDelivered,
          unitPrice: materialUnitPrices[zone.material.id] * zone.length * orderPocketSpring.pocketSpring.width,
          material: {
            name: getSpringMaterialPrintableName(zone.material),
            total: 0,
            weight: 0
          }
        }))
      );
    return pricedZones;
  };

  const getSpringMaterialIdIndexedUnitPrices = async (): Promise<number[]> => {
    const materialUnitPrices: number[] = [];
    if (priceListId !== null) {
      const springMaterialPrices = await springMaterialCustomerPriceApi.fetchSpringMaterialCustomerPrices(customerId, priceListId);
      springMaterialPrices.forEach(springMaterialPrice => materialUnitPrices[springMaterialPrice.material.id] = springMaterialPrice.unitPrice);
    }
    return materialUnitPrices;
  };

  const fetchKitMetrics = async (orderKits: OrderItem[]) => {
    const kits : KitMetric[] = [];
    for (const orderKit of orderKits) {
      const kitId = orderKit.itemId;
      const kit : KitMetric = await http.get(`/api/customers/${customerId}/price-lists/${priceListId}/kits/${kitId}`);
      kits.push({ packagingPrice : orderKit.quantityDelivered * kit.packagingPrice });
    }
    return kits;
  };

  const fetchKitFormOrderItems = async (orderKits: OrderItem[], formMetrics: IdentifiableMetricsMap) => {
    const kitFormOrderItems : OrderItem[] = [];
    for (const orderKit of orderKits) {
      const kitId = orderKit.itemId;
      const quantityOrdered = orderKit.quantityOrdered;
      const quantityDelivered = orderKit.quantityDelivered;
      const kitForms : any[] = await http.get(`/api/customers/${customerId}/price-lists/${priceListId}/kits/${kitId}/forms`);
      kitForms.forEach(kitForm => {
        const form = kitForm.form;
        const formId = form.id;
        const material = form.material;
        const quantity = kitForm.quantity;
        kitFormOrderItems.push({
          name: form.name,
          itemId: formId,
          quantityOrdered: quantityOrdered * quantity,
          quantityDelivered: quantityDelivered * quantity,
          unitPrice: formMetrics[formId].unitPrice,
          material: {
            name: material.name,
            weight: form.weight,
            total: 0
          }
        });
      });
    }
    return kitFormOrderItems;
  };


  const fetchKitShapeOrderItems = async (orderKits: OrderItem[], shapeMetrics: IdentifiableMetricsMap) => {
    const kitShapeOrderItems : OrderItem[] = [];
    for (const orderKit of orderKits) {
      const kitId = orderKit.itemId;
      const quantityOrdered = orderKit.quantityOrdered;
      const quantityDelivered = orderKit.quantityDelivered;
      const kitShapes : any[] = await http.get(`/api/customers/${customerId}/price-lists/${priceListId}/kits/${kitId}/shapes`);
      kitShapes.forEach(kitShape => {
        const shape = kitShape.shape;
        const shapeId = shape.id;
        const material = shape.material;
        const quantity = kitShape.quantity;
        kitShapeOrderItems.push({
          name: shape.name,
          itemId: shapeId,
          quantityOrdered: quantityOrdered * quantity,
          quantityDelivered: quantityDelivered * quantity,
          unitPrice: shapeMetrics[shapeId].unitPrice,
          material: {
            name: material.name,
            weight: shape.unitWeight,
            total: 0
          }
        });
      });
    }
    return kitShapeOrderItems;
  };

  const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);

  const calculateTotal = (orderItems: PrintableOrderItem[]) =>
    sum(orderItems.map(orderItem => orderItem.unitPrice * (orderItem.quantityDelivered || 0)));

  const calculateOrderMaterials = async (
    orderForms: OrderItem[],
    formMetrics: IdentifiableMetricsMap,
    orderShapes: OrderItem[],
    shapeMetrics: IdentifiableMetricsMap,
    orderBonnellSprings: PrintableOrderItem[],
    orderedPocketSpringZones: PrintableOrderItem[]
  ) => {
    const materialNames = [...orderForms, ...orderShapes, ...orderBonnellSprings, ...orderedPocketSpringZones]
      .map(orderItem => orderItem.material?.name)
      .filter(isString);
    const orderMaterials = getUniqueValues(materialNames).map(name => ({ name, weight: 0, total: 0 }));
    orderForms.forEach(orderForm => fillMaterialWeightAndPrice(orderMaterials, orderForm, formMetrics));
    orderShapes.forEach(orderShape => fillMaterialWeightAndPrice(orderMaterials, orderShape, shapeMetrics));
    orderBonnellSprings.forEach(orderBonnellSpring => fillBonnellSpringMaterialPrice(orderMaterials, orderBonnellSpring));
    orderedPocketSpringZones.forEach(orderedPocketSpringZone => fillPocketSpringZoneMaterialPrice(orderMaterials, orderedPocketSpringZone));
    return orderMaterials;
  };

  const getMetricsMap = async (orderItemType: string) => {
    const identifiables : MetricsIdentifiable[] = await http.get(`/api/customers/${customerId}/price-lists/${priceListId}/${orderItemType}?state=ACTIVE,DELETED`);
    return identifiables.reduce(mapMetricsToId, createEmptyMetricsMap());
  };

  const createEmptyMetricsMap = (): IdentifiableMetricsMap => ({});

  const mapMetricsToId = (map: IdentifiableMetricsMap, identifiable: MetricsIdentifiable) => {
    const weight = identifiable.weight || identifiable.unitWeight;
    const price = identifiable.price || identifiable.unitPrice;
    if (price === undefined) {
      console.info(`## No price found for ${JSON.stringify(identifiable)}`);
    } else {
      map[identifiable.id] = {
        weight,
        unitPrice: price.effective
      }
    }
    return map;
  };

  const isString = (value: string | undefined) : value is string => !!value;

  const fillMaterialWeightAndPrice = (orderMaterials: OrderMaterial[], orderItem: OrderItem, itemMetrics: IdentifiableMetricsMap) => {
    const orderMaterial = orderMaterials.find(orderMaterial => orderMaterial.name === orderItem.material?.name);
    const orderItemMetrics = itemMetrics[orderItem.itemId]
    if (orderMaterial !== undefined) {
      const weight = orderMaterial.weight + (orderItemMetrics.weight * orderItem.quantityDelivered);
      orderMaterial.weight = Number(weight.toFixed(3));
      const total = orderMaterial.total + (orderItem.unitPrice * orderItem.quantityDelivered);
      console.debug(`Material: ${orderMaterial.name} + ${orderItem.unitPrice} * ${orderItem.quantityDelivered}`);
      orderMaterial.total = Number(total.toFixed(2));
    }
  };

  const fillSpringOrderItemMaterialPrice = (
    springOrderMaterials: OrderMaterial[],
    springOrderItem: PrintableOrderItem
  ) => {
    const springMaterial = springOrderItem.material;
    if (springMaterial !== undefined) {
      const material = springOrderMaterials.find(material => material.name === springMaterial.name);
      const quantityDelivered = springOrderItem.quantityDelivered;
      if (material !== undefined && quantityDelivered !== undefined) {
        material.total += springOrderItem.unitPrice * quantityDelivered;
      }
    }
  };

  const fillBonnellSpringMaterialPrice = fillSpringOrderItemMaterialPrice;

  const fillPocketSpringZoneMaterialPrice = fillSpringOrderItemMaterialPrice;

  const getUniqueValues = (array: string[]) => array.filter((value, index, array) => array.indexOf(value) === index);

  const onOrderSave = (data: any) => {
    setOrderId(data.id);
    if (isOrderNumberGeneratedAutomatically) {
      setAutomaticallyGeneratedOrderNumber(data.number);
      setOrderNumberGeneratedAutomatically(false);
    }
  };

  const handleOrderNumberGeneratedAutomaticallyChanged
    = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setOrderNumberGeneratedAutomatically(checked);

  const handleOrderStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const orderStatus = event.target.value as OrderStatus;
    if ([OrderStatus.InProgress, OrderStatus.Complete].includes(orderStatus)) {
      setOrderStatus(orderStatus);
    } else {
      console.warn(`Cannot set order to unacceptable status: ${orderStatus}`);
    }
  };

  const handlePriceListSelected = (priceListId: number) => {
    setPriceListId(priceListId);
  };

  const renderFields = (
    initialData: Order|undefined,
    handleSaveClicked: (onSave?: (data: any) => void) => void
  ) => (
    <>
      <h2>Wprowadź dane zamówienia</h2>
      <PriceListSelection
        defaultPriceListId={ priceListId === null ? undefined : priceListId }
        onPriceListSelected={ handlePriceListSelected }
        disabled={ isModificationDisabled }
      />
      <FormControlLabel
        label='Automatycznie generuj numer zamówienia'
        control={<Checkbox
          checked={ isOrderNumberGeneratedAutomatically }
          onChange={ handleOrderNumberGeneratedAutomaticallyChanged }
          disabled={ isOrderAlreadyCreated }
        />}
      />
      <TextInputField
          label='Numer zamówienia'
          autoFocus
          initialValue={ initialData?.number || automaticallyGeneratedOrderNumber?.toString() }
          onValueChange={ setNumber }
          disabled={ isOrderNumberGeneratedAutomatically || isModificationDisabled }
      />
      <DateInputField
        label='Data zamówienia'
        initialValue={ initialData?.creationDate || getCurrentDate() }
        onValueChange={ setCreationDate }
        disabled={ isModificationDisabled }
      />
      <DateInputField
        label='Data realizacji'
        initialValue={ initialData?.completionDate }
        onValueChange={ setCompletionDate }
        disabled={ isModificationDisabled }
      />
      <FormControl>
        <InputLabel id="orderStatus-label">Status</InputLabel>
        <Select
          labelId="orderStatus-label"
          value={ orderStatus || OrderStatus.Created }
          onChange={ handleOrderStatusChange }
          disabled={ !isOrderAlreadyCreated || isModificationDisabled }>
            <MenuItem value={OrderStatus.Created}>utworzone</MenuItem>
            <MenuItem value={OrderStatus.InProgress}>w trakcie</MenuItem>
            <MenuItem value={OrderStatus.Complete}>zakończone</MenuItem>
        </Select>
      </FormControl>
      
      { effectiveOrderId &&
        <>
          <Button
            color='primary'
            className={ classes.button }
            onClick={ handlePrintoutsButtonClicked }
            variant='contained'
          >
            Wydruki
          </Button>
          <Menu open={isPrintoutsMenuOpen} anchorEl={printoutsMenuAnchorElement} onClose={() => closePrintoutsMenu()}>
            <MenuItem onClick={() => handlePrintInternalOrderButtonClicked(effectiveOrderId, false)}>
              Drukuj zamówienie dla produkcji (bez składu kompletów)
            </MenuItem>
            <MenuItem onClick={() => handlePrintInternalOrderButtonClicked(effectiveOrderId, true)}>
              Drukuj zamówienie dla produkcji (ze składem kompletów)
            </MenuItem>
            <MenuItem onClick={() => handlePrintOfficeOrderButtonClicked(effectiveOrderId)}>
              Drukuj zamówienie dla biura
            </MenuItem>
            <MenuItem onClick={() => handlePrintCustomerOrderButtonClicked(effectiveOrderId, false, false)}>
              Drukuj zamówienie dla klienta (bez specyfikacji materiałów)
            </MenuItem>
            <MenuItem onClick={() => handlePrintCustomerOrderButtonClicked(effectiveOrderId, true, false)}>
              Drukuj zamówienie dla klienta (ze specyfikacją materiałów)
            </MenuItem>
          </Menu>
        </>
      }
      { !effectiveOrderId &&
        <Button
          color='primary'
          className={classes.button}
          onClick={() => handleSaveClicked(onOrderSave)}
          variant='contained'
          disabled={ isModificationDisabled }
        >
          Zapisz i kontynuuj dodawanie do zamówienia
        </Button>
      }
    </>
  );

  const initializeFields = (data: Order, initial: boolean) => {
    const status = data.status;
    setNumber(data.number);
    setCreationDate(data.creationDate);
    setCompletionDate(data.completionDate);
    const priceListId = data.priceList.id;
    setPriceListId(priceListId);
    setOrderStatus(status);
    setModificationDisabled(status === 'COMPLETE');
  };

  const collectData = (): Order|null =>
    (number || isOrderNumberGeneratedAutomatically) && creationDate && priceListId !== null
      ? {
        number,
        creationDate,
        completionDate,
        priceList: { id: priceListId },
        status: orderStatus || OrderStatus.Created
      }
      : null;

  const validateOrder = (order: Order) : Promise<boolean> => {
    const completeOrderIsValid = order.status !== 'COMPLETE' || !!completionDate;
    if (!completeOrderIsValid) {
      props.reportAlert && props.reportAlert('Nie ustawiono daty realizacji przed zamknięciem zamówienia');
    }
    return Promise.resolve(completeOrderIsValid);
  };

  const renderOrderItems = (fetchDetails: (initial: boolean) => void) => (
    <>
      { effectiveOrderId && priceListId !== null && <>
        <OrderItemsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          orderId={ effectiveOrderId }
          orderItemType='form'
          title='Formatki'
          promptMessage='Dodaj formatki do zamówienia'
          modificationDisabled={ isModificationDisabled }
          filteringByDimensionEnabled={ true }
        />
        <OrderItemsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          orderId={ effectiveOrderId }
          orderItemType='shape'
          title='Wykroje'
          promptMessage='Dodaj wykroje do zamówienia'
          modificationDisabled={ isModificationDisabled }
          filteringByDimensionEnabled={ true }
        />
        <OrderItemsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          orderId={ effectiveOrderId }
          orderItemType='kit'
          title='Komplety'
          promptMessage='Dodaj komplety do zamówienia'
          modificationDisabled={ isModificationDisabled }
          filteringByDimensionEnabled={ false }
        />
        <OrderItemsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          orderId={ effectiveOrderId }
          orderItemType='bonnell-spring'
          orderItemElementName='bonnellSpring'
          title='Sprężyny bonnell'
          promptMessage='Dodaj sprężyny bonnell do zamówienia'
          modificationDisabled={ isModificationDisabled }
          filteringByDimensionEnabled={ false }
        />
        <OrderItemsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          orderId={ effectiveOrderId }
          orderItemType='pocket-spring'
          orderItemElementName='pocketSpring'
          title='Sprężyny kieszeniowe'
          promptMessage='Dodaj sprężyny kieszeniowe do zamówienia'
          modificationDisabled={ isModificationDisabled }
          filteringByDimensionEnabled={ false }
        />
      </>}
    </>);

  return (
    <DetailsDialog<Order>
      { ...props }
      classes={ classes }
      instanceId={ effectiveOrderId }
      mode={ mode }
      resource={ `customers/${props.customerId}/orders` }
      modificationDisabled={ isModificationDisabled }
      renderFields={ renderFields }
      initializeFields={ initializeFields }
      collectData={ collectData }
      renderBottomRegion={ renderOrderItems }
      validateData={ validateOrder }
    />
  );
}